import { type ReactNode } from 'react';

import { cn } from '@/utils/cn';

export interface CheckoutFrameProps {
  banner?: ReactNode | undefined;
  header?: ReactNode | undefined;
  children: ReactNode;
  footer: ReactNode;
  /** When set, this element is the document `main`. Otherwise children render as a fragment host. */
  mainProps?: { id: string; tabIndex: number } | undefined;
}

/**
 * The hosted-checkout panel. On a phone it is the page; from `sm` up it sits as a bordered card on
 * the merchant-tinted canvas (`--vp-surface-muted`). Borders, not shadows — the card must read in
 * both schemes.
 */
export function CheckoutFrame({ banner, header, children, footer, mainProps }: CheckoutFrameProps) {
  return (
    <div
      className={cn(
        'mx-auto flex w-full flex-1 flex-col',
        'sm:max-w-md sm:justify-center sm:px-4 sm:py-5 md:max-w-lg lg:max-w-xl',
      )}
    >
      <div
        className={cn(
          'bg-background text-text flex flex-1 flex-col',
          'sm:flex-none sm:rounded-lg sm:border sm:border-border',
        )}
      >
        {banner}
        {header}
        {mainProps !== undefined ? (
          <main
            id={mainProps.id}
            tabIndex={mainProps.tabIndex}
            className="flex min-h-0 w-full flex-1 flex-col px-4 py-4 sm:px-5"
          >
            {children}
          </main>
        ) : (
          <div className="flex min-h-0 w-full flex-1 flex-col px-4 py-4 sm:px-5">{children}</div>
        )}
        {footer}
      </div>
    </div>
  );
}
